import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useMotionValue, useMotionValueEvent } from "framer-motion";

export default function CoffeeView({ className }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  // Setup Framer Motion's useScroll hook tracked to the parent wrapper container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // A motion value to hold the smoothly LERP-ed scroll progress
  const interpolatedProgress = useMotionValue(0);

  // Keep track of scroll & LERP states using refs
  const targetProgress = useRef(0);
  const currentProgress = useRef(0);
  const isSeeking = useRef(false);
  const pendingTime = useRef(null);

  // Sync scrollYProgress changes to targetProgress ref
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    targetProgress.current = latest;
  });

  // Smooth text slide-in properties based on interpolatedProgress (LERPed progress)
  // Left side text: slides in from left (-150px) to resting state (0px)
  const leftX = useTransform(interpolatedProgress, [0.1, 0.5, 1], ["-150px", "0px", "0px"]);
  const leftOpacity = useTransform(interpolatedProgress, [0.1, 0.5, 1], [0, 1, 1]);

  // Right side text: slides in from right (150px) to resting state (0px)
  const rightX = useTransform(interpolatedProgress, [0.1, 0.5, 1], ["150px", "0px", "0px"]);
  const rightOpacity = useTransform(interpolatedProgress, [0.1, 0.5, 1], [0, 1, 1]);

  // Single requestAnimationFrame render loop to LERP progress and update video frame
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Ensure video is initially paused to let scroll handle playback time
    video.pause();
    video.preload = "auto";

    let rafId;

    const updateFrame = () => {
      const target = targetProgress.current;
      const current = currentProgress.current;
      const diff = target - current;

      // Smooth LERP interpolation
      if (Math.abs(diff) > 0.0001) {
        currentProgress.current += diff * 0.08;
      } else if (current !== target) {
        currentProgress.current = target;
      }

      // Update the motion value so text animations stay in perfect organic lock-step
      interpolatedProgress.set(currentProgress.current);

      // Frame-accurate seeking queue
      if (video.duration && !isNaN(video.duration)) {
        const targetTime = currentProgress.current * video.duration;
        const boundedTime = Math.max(0, Math.min(targetTime, video.duration - 0.02));

        if (!isSeeking.current) {
          isSeeking.current = true;
          video.currentTime = boundedTime;
        } else {
          pendingTime.current = boundedTime;
        }
      }

      rafId = requestAnimationFrame(updateFrame);
    };

    // Callback when the video finishes seeking a frame
    const handleSeeked = () => {
      isSeeking.current = false;
      if (pendingTime.current !== null) {
        const nextTime = pendingTime.current;
        pendingTime.current = null;
        isSeeking.current = true;
        video.currentTime = nextTime;
      }
    };

    video.addEventListener("seeked", handleSeeked);

    // Initial frame sync once metadata is loaded
    const syncInitialFrame = () => {
      if (video.duration) {
        const initialTime = targetProgress.current * video.duration;
        video.currentTime = Math.max(0, Math.min(initialTime, video.duration - 0.02));
      }
    };
    video.addEventListener("loadedmetadata", syncInitialFrame);

    // Start the loop
    rafId = requestAnimationFrame(updateFrame);

    return () => {
      cancelAnimationFrame(rafId);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("loadedmetadata", syncInitialFrame);
    };
  }, [interpolatedProgress]);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div
        ref={containerRef}
        className={"relative w-full h-[350vh] bg-neutral-950 " + (className || "")}
        id="coffe-view-container"
      >
        {/* Sticky full-screen wrapper so content remains in viewport during scroll progress */}
        <section
          id="coffe-view-section"
          className="sticky top-0 w-full h-screen overflow-hidden flex flex-col justify-end pb-[12%] px-6 md:px-20"
        >
          {/* Background Video Layer */}
          <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
            <video
              ref={videoRef}
              playsInline
              muted
              loop
              preload="auto"
              className="w-full h-full object-cover object-top"
            >
              <source
                src="https://cdn.jiro.build/Laounge%20Coffe/Videos/Coffe%20vIew.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
            {/* Subtle vignette or overlay to ensure readability */}
            <div className="absolute inset-0 bg-black/10 z-10 pointer-events-none" />
          </div>

          {/* Foreground Content Row */}
          <div className="relative z-20 w-full flex flex-col md:flex-row justify-between items-end gap-10 md:gap-0 pointer-events-none">
            {/* Left Side Typography Layout (Slides in from Left) */}
            <motion.div
              style={{
                x: leftX,
                opacity: leftOpacity,
                width: "100%",
                maxWidth: "303px",
              }}
              className="pointer-events-auto"
            >
              <h2
                style={{
                  color: "#FFF",
                  fontFamily: '"Archivo", sans-serif',
                  fontSize: "clamp(36px, 5.5vw, 60px)",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "clamp(40px, 6vw, 64.027px)",
                }}
                className="tracking-tight uppercase"
              >
                RICH &<br />
                EROMATIC<br />
                LOUNGE<br />
                COFFE
              </h2>
            </motion.div>

            {/* Right Side Typography Layout (Slides in from Right) */}
            <motion.div
              style={{
                x: rightX,
                opacity: rightOpacity,
                width: "100%",
                maxWidth: "344px",
              }}
              className="pointer-events-auto text-left md:text-right"
            >
              <p
                style={{
                  color: "#FFF",
                  fontFamily: '"Archivo", sans-serif',
                  fontSize: "clamp(26px, 4vw, 45px)",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "clamp(30px, 4.5vw, 48.286px)",
                }}
                className="md:ml-auto"
              >
                It's a emotion for<br />
                us and we always
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}